import { Info, ExternalLink } from 'react-feather';
import styled from '@emotion/styled';
import type { WidgetSourceMetadata, QuantAnalysisWidgetSourceMetadata } from '@trinity/chat';
import { WidgetSourceType } from '@trinity/chat';

type WidgetSourceInfoProps = {
  sourceMetadata: WidgetSourceMetadata | null | undefined;
  projectSlug?: string | null;
};

export const WidgetSourceInfo = ({ sourceMetadata, projectSlug }: WidgetSourceInfoProps) => {
  if (!sourceMetadata) return null;

  if (sourceMetadata.type === WidgetSourceType.QuantAnalysis) {
    return <QuantAnalysisSourceView sourceMetadata={sourceMetadata} projectSlug={projectSlug} />;
  }

  // Future: Handle other source types
  return null;
};

type QuantAnalysisSourceViewProps = {
  sourceMetadata: QuantAnalysisWidgetSourceMetadata;
  projectSlug?: string | null;
};

const QuantAnalysisSourceView = ({ sourceMetadata, projectSlug }: QuantAnalysisSourceViewProps) => {
  // Access the quantAnalysis property which is guaranteed to exist for QuantAnalysisWidgetSourceMetadata

  const { quantAnalysis } = sourceMetadata;

  const renderFilters = () => {
    if (!quantAnalysis.filters) return null;

    const { columnNames: rawColumnNames, rowNames: rawRowNames } = quantAnalysis.filters;

    const columnNames = normalizeToMatrix(rawColumnNames);
    const rowNames = normalizeToMatrix(rawRowNames);

    if (columnNames.length === 0 && rowNames.length === 0) {
      return null;
    }

    return (
      <SourceRow>
        <SourceLabel>Data Cuts:</SourceLabel>
        <SourceValue>
          {columnNames.length > 0 && <span>Columns: {columnNames.map((p) => p.join(' > ')).join(', ')}</span>}
          {rowNames.length > 0 && (
            <span>
              {columnNames.length > 0 && ' | '}
              Rows: {rowNames.map((p) => p.join(' > ')).join(', ')}
            </span>
          )}
        </SourceValue>
      </SourceRow>
    );
  };

  const navigationUrl = projectSlug
    ? (() => {
        const params = new URLSearchParams({ analysisItemId: quantAnalysis.itemId.toString() });

        return `/projects/${projectSlug}/quant-analysis/${quantAnalysis.quantAnalysisId}?${params.toString()}`;
      })()
    : null;

  return (
    <Container>
      <IconContainer>
        <Info size={14} />
      </IconContainer>
      <SourceContent>
        <SourceRow>
          <SourceLabel>Project Name:</SourceLabel>
          <SourceValue>{quantAnalysis.projectName}</SourceValue>
        </SourceRow>
        <SourceRow>
          <SourceLabel>Analysis:</SourceLabel>
          <SourceValue>{quantAnalysis.itemName}</SourceValue>
        </SourceRow>
        <SourceRow>
          <SourceLabel>File:</SourceLabel>
          <SourceValue>{quantAnalysis.quantAnalysisName}</SourceValue>
        </SourceRow>
        {renderFilters()}
        {navigationUrl && (
          <ViewSourceLink href={navigationUrl} target="_blank" rel="noopener noreferrer">
            View source
            <ExternalLink size={12} style={{ marginLeft: 4 }} />
          </ViewSourceLink>
        )}
      </SourceContent>
    </Container>
  );
};

function normalizeToMatrix(value: string[] | string[][] | undefined): string[][] {
  if (!value || value.length === 0) return [];
  return Array.isArray(value[0]) ? (value as string[][]) : [value as string[]];
}

const Container = styled.div(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '8px',
  padding: '8px 12px',
  marginTop: '16px',
  marginBottom: '8px',
  backgroundColor: theme.palette?.gray?.light7 ?? 'var(--gray-xl, #f8f8f8)',
  borderRadius: '4px',
  border: `1px solid ${theme.palette?.gray?.light4 ?? 'var(--gray-l, #e0e0e0)'}`,
  fontSize: '12px',
  lineHeight: '16px',
}));

const IconContainer = styled.div(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  color: theme.palette?.gray?.main ?? 'var(--gray, #999)',
  marginTop: '1px',
}));

const SourceContent = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  flex: 1,
  minWidth: 0,
});

const SourceRow = styled.div({
  display: 'flex',
  gap: '6px',
  alignItems: 'baseline',
});

const SourceLabel = styled.span(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette?.gray?.dark1 ?? 'var(--gray-d, #666)',
  flexShrink: 0,
  minWidth: 'fit-content',
}));

const SourceValue = styled.span(({ theme }) => ({
  color: theme.palette?.text?.primary ?? 'var(--black, #333)',
  wordBreak: 'break-word',
  flex: 1,
}));

const ViewSourceLink = styled.a(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  fontSize: '11px',
  color: theme.palette?.primary?.main ?? 'var(--pri-02, #4A90E2)',
  textDecoration: 'none',
  marginTop: '2px',
  '&:hover': {
    textDecoration: 'underline',
  },
}));
